import { useCallback, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import AskAiChatRepository from '@/creditflowresearch/infrastructure/repository/AskAiChatRepository';
import { AskAiProduct } from '@/creditflowresearch/domain/vo/AskAiChat';
import { downloadBlob } from '../helpers/downloadBlob';

const getExportKey = (messageId: string, product?: AskAiProduct): string =>
  product ? `${ messageId }:${ product }` : messageId;

interface IParams {
  chatId: string | null,
}

const useAskAiChatExport = ({ chatId }: IParams) => {
  const [exportingKey, setExportingKey] = useState<string | null>(null);

  const repository = getContainer().get<AskAiChatRepository>(AskAiChatRepository);

  const exportData = useCallback(async (
    messageId: string,
    product?: AskAiProduct,
  ): Promise<void> => {
    if (!chatId) return;

    setExportingKey(getExportKey(messageId, product));

    try {
      const { filename, content } = await repository.export(chatId, messageId, product);

      downloadBlob(filename, new Blob([content], { type: 'text/csv;charset=utf-8;' }));
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setExportingKey(null);
    }
  }, [chatId, repository]);

  const isExporting = useCallback((messageId: string, product?: AskAiProduct): boolean =>
    exportingKey === getExportKey(messageId, product), [exportingKey]);

  return { exportData, isExporting };
};

export default useAskAiChatExport;
